import { ShieldRounded } from "@mui/icons-material";
import { Grid, Paper, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { API, action, controller } from "../../../api/api";
import { Button, InputOtp } from "../../../controls";
import { Form } from "../../../layout";
import { ToFormData } from "../../../hooks/Method";

/**
 * First-time two-factor setup, shown when a System Engineer has required 2FA for
 * an account that has not enrolled yet.
 *
 * The secret is generated server-side and shown here ONCE. An administrator never
 * sees it — they can require, reset or disable 2FA, but only the account holder
 * ever holds the factor.
 */
const TwoFactorEnroll = ({ enrollment, onEnrolled, onCancel }) => {
  const { t } = useTranslation(["common", "validation"]);
  const [stage, setStage] = useState(enrollment?.method ? "setup" : "choose");
  const [data, setData] = useState(enrollment);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const choose = (method) => {
    setLoading(true);
    setError("");
    API(controller.Auth + action.TwoFactorEnrollStart).post(
      ToFormData({ challengeToken: enrollment.challengeToken, method }),
      (res) => {
        setLoading(false);
        if (!res) return;
        if (res.status === 200) {
          setData({ ...res.result, challengeToken: enrollment.challengeToken });
          setStage("setup");
        } else {
          setError(res.msg);
        }
      },
    );
  };

  const confirm = (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(t("validation:verificationCodeRequired", "Enter the verification code"));
      return;
    }
    setLoading(true);
    setError("");
    API(controller.Auth + action.TwoFactorEnrollConfirm).post(
      ToFormData({ challengeToken: data.challengeToken, code: code.trim() }),
      (res) => {
        setLoading(false);
        if (!res) return;
        if (res.status === 200) {
          onEnrolled(res.result);
          return;
        }
        setError(res.msg);
        setCode("");
      },
    );
  };

  const isApp = Number(data?.method) === 1;

  return (
    // spacing={2} rather than the login form's 3: this screen carries a QR plus two
    // blocks of instructions, and at 3 the card runs past the fold on a laptop.
    <Grid container spacing={2}>
      <Grid item xs={12} className="tx-center">
        <div className="d-flex j-center mt-10">
          <div
            className="w-fit p-5"
            style={{ backgroundColor: "var(--primaryXS-color)", borderRadius: "50%" }}
          >
            <ShieldRounded className="primary-color" sx={{ fontSize: "40px" }} />
          </div>
        </div>
        <Typography variant="h6" className="mt-10">
          {t("twoFactorSetupTitle", "Set up two-step verification")}
        </Typography>
        <Typography variant="body2" className="gray-color">
          {t(
            "twoFactorSetupHint",
            "Your administrator requires a second step when you sign in.",
          )}
        </Typography>
      </Grid>

      {error && (
        <Grid item xs={12} className="tx-center">
          <Typography color="error">{error}</Typography>
        </Grid>
      )}

      {stage === "choose" && (
        <>
          {data?.allowAuthenticatorApp && (
            <Grid item xs={12}>
              <Button
                loading={loading}
                fullWidth
                size="large"
                onClick={() => choose(1)}
              >
                {t("useAuthenticatorApp", "Use an authenticator app")}
              </Button>
            </Grid>
          )}
          {data?.allowEmail && (
            <Grid item xs={12}>
              <Button
                loading={loading}
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => choose(2)}
              >
                {t("useEmailCode", "Email me a code")}
              </Button>
            </Grid>
          )}
          {!data?.allowAuthenticatorApp && !data?.allowEmail && (
            <Grid item xs={12} className="tx-center">
              <Typography color="error">
                {t(
                  "twoFactorNoMethod",
                  "No verification method is available for your account. Contact your System Engineer.",
                )}
              </Typography>
            </Grid>
          )}
        </>
      )}

      {stage === "setup" && (
        <Grid item xs={12}>
          <Form noValidate onSubmit={confirm}>
            <Grid container spacing={2}>
              {isApp && data?.otpAuthUri && (
                <>
                  {/* The wrapper does the centring with flex. `tx-center` alone does
                      not centre the Paper: it is a block element, so it fills the row
                      and the SVG sits at its left edge. */}
                  <Grid item xs={12}>
                    <div className="d-flex j-center">
                      {/* Only a hairline of white here. A QR needs a quiet zone to
                          scan, but this sits on an already-white card, so the card
                          supplies it — the padding was invisible and just made the
                          code smaller. The white backdrop stays so the code still
                          reads if the card is ever themed. */}
                      <Paper
                        style={{ background: "#fff", lineHeight: 0, padding: 4 }}
                        elevation={0}
                      >
                        {/* Rendered client-side: the secret never travels to a
                            third-party QR service. */}
                        <QRCodeSVG value={data.otpAuthUri} size={200} level="M" />
                      </Paper>
                    </div>
                  </Grid>
                  <Grid item xs={12} className="tx-center">
                    <Typography variant="caption" className="gray-color d-block">
                      {t(
                        "twoFactorScanHint",
                        "Scan this with Google Authenticator, Microsoft Authenticator or Authy.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      className="gray-color d-block"
                      style={{ marginTop: 6 }}
                    >
                      {t("orEnterKeyManually", "Or enter this key manually:")}
                    </Typography>
                    <Typography
                      variant="caption"
                      className="d-block"
                      style={{
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {data.secret}
                    </Typography>
                  </Grid>
                </>
              )}

              {!isApp && (
                <Grid item xs={12} className="tx-center">
                  <Typography variant="body2" className="gray-color">
                    {t("twoFactorEmailHint", "We sent a 6-digit code to {{email}}.", {
                      email: data?.sentTo || "",
                    })}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <InputOtp
                  name="code"
                  value={code}
                  onChange={setCode}
                  autoFocus
                  invalid={!!error}
                />
              </Grid>

              <Grid item xs={12}>
                <Button loading={loading} type="submit" fullWidth size="large">
                  {t("confirmAndSignIn", "Confirm and sign in")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        </Grid>
      )}

      {/* Matches the Back action on the verify and forgot-password screens. */}
      <Grid item xs={12} className="!pt-[6px]">
        <Button
          variant="text"
          size="medium"
          fullWidth
          onClick={onCancel}
          className="!text-gray-500"
        >
          {t("backToLogin", "Back to sign in")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default TwoFactorEnroll;
